import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2 } from 'lucide-react';

import { Button } from 'components/ui/button';
import { ScrollArea } from 'components/ui/scroll-area';
import { cn } from 'lib/utils';
import { questionNumber } from './data';
import { Checkbox } from 'components/ui/checkbox';
import { SYMPTOM_CODE } from 'lib/symptom-checker';
import { useSymptomStore } from 'store/symptom';
import { getSymptomQuestion } from 'services/symptom-checker/api';
import { PATH_ROUTES } from 'routes/path';

interface SymptomWrapperProps {
  children: React.ReactNode;
}

const noSidebar = [SYMPTOM_CODE.CONFIRMATION, SYMPTOM_CODE.OTHER_BODY_PART];

export default function SymptomWrapper({ children }: SymptomWrapperProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');

  const { t } = useTranslation();

  const questionData = useSymptomStore((store) => store.questionData)?.data;
  const setQuestionData = useSymptomStore((store) => store.setQuestionData);

  const [isLoading, setIsLoading] = useState(false);

  const onBack = async () => {
    if (code) {
      return navigate(PATH_ROUTES.GENERATE_SYMPTOM_REVIEW);
    }

    if (!questionData?.q_id) {
      return navigate(PATH_ROUTES.SYMPTOM_CHECKER);
    }

    setIsLoading(true);
    const response = await getSymptomQuestion({
      type: 'previous',
      q_id: questionData?.q_id || '',
    });

    if (!response?.data) {
      setQuestionData(undefined);
      return navigate(PATH_ROUTES.SYMPTOM_CHECKER);
    }

    if (response?.data?.q_id) {
      setQuestionData(response);
      setIsLoading(false);
    }
  };

  const questionTitle: Record<SYMPTOM_CODE, string> = useMemo(
    () => ({
      [SYMPTOM_CODE.SELECT_BODY_PART]: t('body_part_title'),
      [SYMPTOM_CODE.OTHER_BODY_PART]: t('body_part_title'),
      [SYMPTOM_CODE.FEEL_SYMPTOMS]: t('feel_symptom_title'),
      [SYMPTOM_CODE.PAIN_LEVEL]: t('pain_level_title'),
      [SYMPTOM_CODE.SYMPTOMS_DURATION]: t('symptom_duration_title'),
      [SYMPTOM_CODE.STARTED_MEDICATION]: t('started_medication_title'),
      [SYMPTOM_CODE.IMAGE_UPLOAD]: t('image_upload_title'),
      [SYMPTOM_CODE.ADDITIONAL_DETAIL]: t('additional_detail_title'),
      [SYMPTOM_CODE.OTHER_SYMPTOMS]: t(''),
      [SYMPTOM_CODE.CONFIRMATION]: 'other_symptoms_title',
    }),
    []
  );

  const sideQuestions: Record<
    SYMPTOM_CODE,
    { label: string; sequence: number }
  > = useMemo(
    () => ({
      [SYMPTOM_CODE.SELECT_BODY_PART]: {
        label: t('body_part_label'),
        sequence: 1,
      },
      [SYMPTOM_CODE.OTHER_BODY_PART]: { label: '', sequence: 1 },
      [SYMPTOM_CODE.FEEL_SYMPTOMS]: {
        label: t('feel_symptom_label'),
        sequence: 2,
      },
      [SYMPTOM_CODE.PAIN_LEVEL]: { label: t('pain_level_label'), sequence: 3 },
      [SYMPTOM_CODE.SYMPTOMS_DURATION]: {
        label: t('symptom_duration_label'),
        sequence: 4,
      },
      [SYMPTOM_CODE.OTHER_SYMPTOMS]: {
        label: t('other_symptoms_label'),
        sequence: 5,
      },
      [SYMPTOM_CODE.STARTED_MEDICATION]: {
        label: t('started_medication_label'),
        sequence: 6,
      },
      [SYMPTOM_CODE.IMAGE_UPLOAD]: {
        label: t('image_upload_label'),
        sequence: 7,
      },
      [SYMPTOM_CODE.ADDITIONAL_DETAIL]: {
        label: t('additional_detail_label'),
        sequence: 8,
      },
      [SYMPTOM_CODE.CONFIRMATION]: { label: '', sequence: 9 },
    }),
    []
  );

  const questionSequence = questionData?.code
    ? questionNumber[questionData?.code]
    : 0;

  return (
    <div className='p-4 space-y-4'>
      <Button
        variant='outline'
        className='h-8 px-6 text-sm rounded-full text-primary hover:text-primary border-primary'
        onClick={onBack}
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader2 className='w-4 h-4 mb-px mr-1 animate-spin' />
        ) : (
          <ArrowLeft className='w-4 h-4 mb-px mr-1' />
        )}{' '}
        {t('go_back')}
      </Button>

      <div className='pt-4 pb-2 border rounded-md shadow-sm'>
        <div className='px-4 pb-2 space-y-2 border-b'>
          <div className='flex items-center gap-2'>
            <div className='flex items-center justify-center text-sm rounded-full size-5 bg-primary/10'>
              <p className='font-semibold text-primary'>{questionSequence}</p>
            </div>
            <h2 className='font-semibold'>
              {questionData?.code ? questionTitle[questionData?.code] : ''}
            </h2>
          </div>
          <div className='space-y-1'>
            <div className='grid grid-cols-8 gap-0.5 md:gap-1'>
              {Array.from({ length: 8 }).map((_, index) => (
                <div
                  key={index.toString()}
                  className={cn(
                    'bg-[#E6E6E6] w-full h-2 rounded-full',
                    questionSequence > index + 1 && 'bg-primary'
                  )}
                />
              ))}
            </div>
            <p className='text-sm md:text-base'>
              {questionSequence - 1} {t('8_questionnaire_completed')}
            </p>
          </div>
        </div>
        <div className='grid grid-cols-12'>
          <div className='hidden col-span-4 md:block lg:col-span-3'>
            <ScrollArea className='h-fit lg:h-[calc(100vh-18rem)]'>
              <div>
                {Object.entries(sideQuestions).map(([key, value]) => {
                  if (noSidebar.includes(key as SYMPTOM_CODE)) return null;

                  return (
                    <div
                      key={key}
                      className='flex items-center gap-2 p-4 border-b'
                    >
                      <Checkbox
                        id={key}
                        checked={value.sequence < questionSequence}
                        disabled
                      />
                      <h2
                        className={cn(
                          'text-sm font-semibold leading-tight',
                          value.sequence < questionSequence && 'text-primary'
                        )}
                      >
                        {value.label}
                      </h2>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
          <div className='col-span-12 md:col-span-8 md:border-l lg:col-span-9'>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
